import { useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import client from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Medal, ShareNetwork, Confetti as ConfettiIcon } from "@phosphor-icons/react";

function Avatar({ name, picture, size = 92 }) {
  return picture
    ? <img src={picture} alt="" style={{ width: size, height: size }} className="rounded-full object-cover" />
    : <span style={{ width: size, height: size }} className="flex items-center justify-center rounded-full bg-gold/[0.16] font-display text-3xl text-gold">{name?.[0]?.toUpperCase()}</span>;
}

export function fireConfetti() {
  const end = Date.now() + 900;
  const colors = ["#D4AF37", "#E8C86A", "#FFFFFF"];
  (function frame() {
    confetti({ particleCount: 4, angle: 60, spread: 60, origin: { x: 0 }, colors });
    confetti({ particleCount: 4, angle: 120, spread: 60, origin: { x: 1 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
  confetti({ particleCount: 100, spread: 85, origin: { y: 0.4 }, colors });
}

export function InviteCelebrationModal({ event, onClose }) {
  if (!event) return null;
  const share = async () => {
    const text = event.badge
      ? `I just unlocked the "${event.badge}" badge on Screening Circle — my friends decide what I watch next! 🍿`
      : `${event.friend_name} just joined my friends on Screening Circle! 🍿`;
    const url = window.location.origin + "/";
    if (navigator.share) { try { await navigator.share({ title: "Screening Circle", text, url }); return; } catch { /* cancelled */ } }
    try { await navigator.clipboard.writeText(`${text} ${url}`); toast.success("Copied — share the moment"); }
    catch { toast.error("Couldn't copy"); }
  };
  return (
    <Dialog open={!!event} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="border-white/10 bg-elevated text-white sm:max-w-sm" data-testid="invite-celebration-modal">
        <DialogHeader>
          <DialogTitle className="sr-only">A friend joined</DialogTitle>
          <DialogDescription className="sr-only">{event.friend_name} joined from your invite</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center py-3 text-center">
          <div className="relative">
            <Avatar name={event.friend_name} picture={event.friend_picture} size={92} />
            <span className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-gold text-gold-ink shadow-lg">
              <ConfettiIcon size={17} weight="fill" />
            </span>
          </div>
          <p className="mt-5 text-[11px] font-extrabold uppercase tracking-[2px] text-gold">Joined from your invite</p>
          <p className="mt-1 font-display text-3xl font-semibold text-[#F6F5F1]" data-testid="celebration-name">{event.friend_name}</p>
          <p className="mt-2 max-w-[17rem] text-[13px] text-dim">
            {event.friend_name?.split(" ")[0]} is now your friend — your taste match sharpens with every title you both rate.
          </p>
          {event.badge && (
            <div className="mt-5 flex items-center gap-2.5 rounded-full border border-gold/30 bg-gold/[0.08] px-4 py-2" data-testid="celebration-badge">
              <Medal size={20} weight="fill" className="text-gold" />
              <span className="text-[13px] font-bold text-[#F6F5F1]">You unlocked <span className="text-gold">{event.badge}</span></span>
            </div>
          )}
          {typeof event.invited_count === "number" && event.invited_count > 0 && (
            <p className="mt-3 text-[12px] text-faint" data-testid="celebration-count">{event.invited_count} friend{event.invited_count === 1 ? "" : "s"} joined from your invites</p>
          )}
          <div className="mt-6 flex items-center gap-2">
            <button onClick={share} data-testid="celebration-share" className="btn-gold flex items-center gap-2 rounded-full px-6 py-3 text-[14px]">
              <ShareNetwork size={16} weight="fill" /> Share the moment
            </button>
            <button onClick={onClose} data-testid="celebration-close" className="rounded-full border border-white/15 px-5 py-3 text-[14px] font-bold text-dim hover:text-[#F6F5F1]">Nice!</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * App-wide watcher: polls for "a friend joined from your invite" events and
 * fires confetti + the celebration modal on ANY page, then marks them seen so
 * they never repeat. Mounted once inside AppLayout.
 */
export function InviteCelebrationWatcher() {
  const [event, setEvent] = useState(null);
  const queue = useRef([]);
  const busy = useRef(false);

  const check = () => {
    if (busy.current) return;
    client.get("/friends/celebrations").then((r) => {
      const events = r.data.results || [];
      if (!events.length) return;
      const ids = events.map((e) => e.id);
      queue.current = events;
      busy.current = true;
      setEvent(events[0]);
      fireConfetti();
      client.post("/friends/celebrations/seen", { ids }).catch(() => {});
    }).catch(() => {});
  };

  useEffect(() => {
    check();
    const t = setInterval(check, 120000);
    return () => clearInterval(t);
  }, []);

  const next = () => {
    const rest = queue.current.slice(1);
    queue.current = rest;
    if (rest.length) { setEvent(rest[0]); fireConfetti(); }
    else { setEvent(null); busy.current = false; }
  };

  return <InviteCelebrationModal event={event} onClose={next} />;
}
